import React, { useCallback, useEffect } from 'react';
import { Frame, Loader, useSageBreakpoint, Icon } from '@kajabi/sage-react';
import * as Sentry from '@sentry/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Stripe } from '@stripe/stripe-js';

import CheckoutStripeElements from 'apps/commerce/common/checkout/components/CheckoutStripeElements';
import CheckoutCartSummary from 'apps/commerce/cart/CheckoutCartSummary';
import CheckoutCartForm from 'apps/commerce/cart/CheckoutCartForm';
import cartClient from 'apps/commerce/cart/utils/cartClient';
import type { Cart } from 'apps/commerce/cart/types';
import CheckoutSettings from 'apps/commerce/common/checkout/types/CheckoutSettings';
import useToast from '../common/checkout/hooks/useToast';
import { useCart } from './CartContext';

import CartSummaryDropdown from './components/CartSummaryDropdown';

interface CheckoutCartProps {
  cart: Cart;
  goBackToCart: () => void;
  submitCheckout: () => void;
  hideModal: () => void;
  isPreview?: boolean;
}

interface ToastHook {
  showToast: (params: { title: string; type: string; icon: string }) => void;
}

const CheckoutCart = ({
  cart,
  goBackToCart,
  submitCheckout,
  hideModal,
  isPreview = false,
}: CheckoutCartProps) => {
  const breakpoints = useSageBreakpoint();
  const { t } = useTranslation();
  const { showToast } = useToast() as ToastHook;
  const { fetchCart } = useCart();
  const { setValue, watch } = useFormContext();

  const { totalPrice } = cart;
  const checkoutSettings: CheckoutSettings = watch('checkoutSettings');
  const stripe: Stripe | null = watch('stripe');
  const isPaypalOnly: boolean | undefined = watch('isPaypalOnly');

  const stripeInitialized = (checkoutSettings && stripe) || isPreview;
  const paypalInitialized = checkoutSettings && isPaypalOnly;
  const isFree = checkoutSettings && totalPrice?.amount === 0;

  const setCheckoutSettings = useCallback(
    (checkoutSettings: CheckoutSettings) => {
      setValue('checkoutSettings', checkoutSettings);
      setValue('paymentMethods', checkoutSettings.paymentMethods);
      setValue('additionalPaymentMethods', checkoutSettings.additionalPaymentMethods);
      setValue('nameRequired', checkoutSettings.nameRequired);
      setValue('phoneRequired', checkoutSettings.phoneNumberRequired);
      setValue('addressRequired', checkoutSettings.addressRequired);
      setValue('serviceAgreementRequired', checkoutSettings.serviceAgreementRequired);
      setValue('serviceAgreementCustomText', checkoutSettings.serviceAgreementCustomText);
      setValue('useSavedCard', !!checkoutSettings.savedCardDetails);
    },
    [setValue],
  );

  const handleFetchError = useCallback(
    (err: Error) => {
      Sentry.captureException(err);
      hideModal();
      showToast({
        title: t('messages.cart.checkout_settings_error'),
        type: 'danger',
        icon: Icon.ICONS.INFO_CIRCLE,
      });
    },
    [hideModal, showToast, t],
  );

  // Fetch the checkoutSettings for cart-specific payment & checkout configuration
  // Stores the checkoutSettings in the form context for use in the CheckoutStripeElements component, and
  // explicitly set the paymentMethods, site and additionalPaymentMethods in the form context for use in the
  // PaymentDetails component (which depends on that data existing in the for)
  const loadCheckoutSettings = useCallback(() => {
    cartClient
      .fetchCheckoutSettings()
      .then((res) => {
        const checkoutSettings: CheckoutSettings = res.data;
        if (!checkoutSettings) {
          handleFetchError(new Error('No checkout settings found'));
          return;
        }
        setCheckoutSettings(checkoutSettings);
      })
      .catch((err) => {
        handleFetchError(err);
      });
  }, [handleFetchError, setCheckoutSettings]);

  useEffect(() => {
    if (isPreview) return;
    fetchCart();
    loadCheckoutSettings();
  }, [fetchCart, isPreview, loadCheckoutSettings]);

  if (paypalInitialized) {
    // Use a div instead of a form when in preview mode to prevent nested form warnings
    const FormElement = isPreview ? 'div' : 'form';
    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <FormElement
          {...(!isPreview && {
            onKeyPress: (e) => e.key === 'Enter' && e.preventDefault(),
            onSubmit: submitCheckout,
          })}
          className="cart-checkout-form"
          data-testid="cart-checkout-form"
        >
          <Frame
            className="checkout-cart"
            direction={Frame.DIRECTIONS.HORIZONTAL}
            gap={Frame.GAPS.XL}
            width={Frame.WIDTHS.FILL}
          >
            {breakpoints.sm && <CartSummaryDropdown cart={cart} />}
            <CheckoutCartForm cart={cart} />
            <CheckoutCartSummary cart={cart} goBackToCart={goBackToCart} />
          </Frame>
        </FormElement>
      </>
    );
  }

  if (stripeInitialized || isFree) {
    // Use a div instead of a form when in preview mode to prevent nested form warnings
    const FormElement = isPreview ? 'div' : 'form';
    return (
      <CheckoutStripeElements
        stripe={stripe}
        attemptReloadWithCardsOnly={false}
        amountPriceInCents={totalPrice?.amount || 0}
        currency={totalPrice?.currency}
        paymentMethods={checkoutSettings.paymentMethods}
        additionalPaymentMethods={checkoutSettings.additionalPaymentMethods}
        vendorAccountId={checkoutSettings.vendorAccountId}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <FormElement
          {...(!isPreview && {
            onKeyPress: (e) => e.key === 'Enter' && e.preventDefault(),
            onSubmit: submitCheckout,
          })}
          className="cart-checkout-form"
          data-testid="cart-checkout-form"
        >
          <Frame
            className="checkout-cart"
            direction={Frame.DIRECTIONS.HORIZONTAL}
            gap={Frame.GAPS.XL}
            width={Frame.WIDTHS.FILL}
          >
            {breakpoints.sm && <CartSummaryDropdown cart={cart} />}
            <CheckoutCartForm cart={cart} />
            <CheckoutCartSummary cart={cart} goBackToCart={goBackToCart} />
          </Frame>
        </FormElement>
      </CheckoutStripeElements>
    );
  }

  return <Loader className="cart-loader" loading={true} />;
};

export default CheckoutCart;
